import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { ArrowRight, MapPin, Calendar, Utensils, Sparkles } from "lucide-react";
import {
  OrnamentDivider,
  Fleuron,
  WaveDivider,
} from "@/components/Ornament";
import { LemonBranchImg, AmalfiCoastImg } from "@/components/WatercolourArt";
import { ScrollReveal } from "@/components/ScrollReveal";

const GARDEN_WATERCOLOR = "/illustrations/garden_watercolor.png";

const factIcons = [Utensils, Calendar, MapPin, Sparkles];

const About = () => {
  const { t } = useLanguage();
  const facts = t("about.facts") || [];

  return (
    <div data-testid="about-page" className="pt-24 md:pt-28 paper-grain bg-sky-roze relative overflow-hidden pb-24">
      {/* Lemon branches – decorative corners, sit over the watercolour sky */}
      <LemonBranchImg className="absolute top-20 -left-6 w-44 md:w-60 lg:w-[22rem] opacity-95 pointer-events-none hidden md:block z-10" />
      <LemonBranchImg flip className="absolute top-20 -right-6 w-44 md:w-60 lg:w-[22rem] opacity-95 pointer-events-none hidden md:block z-10" />

      {/* Header band — full-width watercolour garden as background, text overlaid */}
      <section className="relative w-full overflow-hidden">
        {/* Watercolour garden — full background */}
        <img
          src={GARDEN_WATERCOLOR}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
          data-testid="about-garden-watercolor"
        />
        {/* Soft white-to-blue gradient overlay on the left so text stays readable */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.6) 32%, rgba(255,255,255,0.2) 55%, rgba(255,255,255,0) 72%)",
          }}
        />

        {/* Content overlay */}
        <div className="relative container-roze min-h-[72vh] md:min-h-[80vh] flex items-end pb-12 md:pb-20">
          <ScrollReveal className="max-w-2xl">
            <div className="overline mb-5 flex items-center gap-3">
              <Fleuron className="h-3 w-3 text-gold" />
              {t("about.eyebrow")}
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-8xl font-light leading-[0.95] tracking-tight">
              {t("about.title")}
            </h1>
            <p className="mt-8 text-lg md:text-xl text-foreground/80 leading-relaxed max-w-xl font-light italic">
              {t("about.lead")}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Story + Facts */}
      <section className="container-roze mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 relative">
        <div className="md:col-span-7 space-y-6 text-base md:text-lg text-foreground/85 leading-[1.85]">
          <ScrollReveal>
            <p data-testid="about-paragraph-1" className="drop-cap font-light">
              {t("about.p1")}
            </p>
          </ScrollReveal>
          <ScrollReveal delay={1}>
            <p data-testid="about-paragraph-2" className="font-light">
              {t("about.p2")}
            </p>
          </ScrollReveal>
          <ScrollReveal delay={2}>
            <p data-testid="about-paragraph-3" className="font-light">
              {t("about.p3")}
            </p>
          </ScrollReveal>

          <ScrollReveal delay={3} className="pt-6">
            <Link
              to="/reserve"
              data-testid="about-reserve-cta"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-xs uppercase tracking-[0.22em]"
            >
              {t("nav.reserve")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
            </Link>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={1} as="aside" className="md:col-span-5 md:pl-6">
          <div className="border border-gold/40 bg-white/60 paper-grain p-8">
            <div className="overline mb-6 flex items-center gap-3">
              <Fleuron className="h-3 w-3 text-gold" />
              {t("about.facts_title")}
            </div>
            <ul className="divide-y divide-border">
              {facts.map((f, idx) => {
                const Icon = factIcons[idx % factIcons.length];
                return (
                  <li
                    key={idx}
                    data-testid={`about-fact-${idx}`}
                    className="py-5 flex items-start gap-5"
                  >
                    <Icon className="h-5 w-5 mt-0.5 text-gold" strokeWidth={1.4} />
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.22em] text-foreground/55">
                        {f.label}
                      </div>
                      <div className="mt-1 font-serif text-2xl font-light leading-tight italic">
                        {f.value}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </ScrollReveal>
      </section>

      {/* Ornament divider */}
      <div className="container-roze py-16 md:py-20">
        <OrnamentDivider />
      </div>

      {/* Closing watercolour band — full-width garden illustration */}
      <section className="relative w-full overflow-hidden">
        <img
          src={GARDEN_WATERCOLOR}
          alt="Roze garden in watercolour"
          className="w-full h-[42vh] md:h-[52vh] object-cover select-none"
        />
        {/* Subtle bottom fade to merge into next section */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(166,206,227,0) 0%, rgba(166,206,227,0.45) 100%)",
          }}
        />
      </section>

      <div className="bg-background pt-12 pb-2">
        <div className="container-roze">
          <WaveDivider className="w-full h-12 text-primary/30" />
        </div>
      </div>
    </div>
  );
};

export default About;
